import { useState, useEffect, useCallback, useRef } from "react";

const API_BASE = "https://digitalbalm.com";
const INACTIVITY_TIMEOUT = 60000;

const ALL_COINS = [
  "BTC","ETH","SOL","BNB","XRP",
  "DOGE","ADA","AVAX","LINK","LTC","XLM",
  "HBAR","SUI","HYPE","ARB","UNI","AAVE",
  "SHIB","PEPE","BONK","WIF"
];

const COIN_TIERS = {
  BTC:"core",ETH:"core",SOL:"core",BNB:"core",XRP:"core",
  DOGE:"momentum",ADA:"momentum",AVAX:"momentum",LINK:"momentum",LTC:"momentum",XLM:"momentum",
  HBAR:"opportunity",SUI:"opportunity",TON:"opportunity",HYPE:"opportunity",
  ARB:"opportunity",UNI:"opportunity",AAVE:"opportunity",
  SHIB:"degen",PEPE:"degen",BONK:"degen",WIF:"degen"
};

const TIER_COLORS = {
  core:"#6366f1", momentum:"#22c55e", opportunity:"#f59e0b", degen:"#ef4444"
};

const TIMEFRAMES = [
  {label:"1D",days:1},{label:"7D",days:7},{label:"30D",days:30},
  {label:"90D",days:90},{label:"6M",days:180},{label:"1Y",days:365},{label:"ALL",days:9999}
];

// Binance US pairs for chart history — same source as agent
const BINANCE_PAIRS = {
  BTC:"BTCUSDT",ETH:"ETHUSDT",SOL:"SOLUSDT",BNB:"BNBUSDT",XRP:"XRPUSDT",
  DOGE:"DOGEUSDT",ADA:"ADAUSDT",AVAX:"AVAXUSDT",LINK:"LINKUSDT",LTC:"LTCUSDT",
  XLM:"XLMUSDT",HBAR:"HBARUSDT",SUI:"SUIUSDT",TON:"TONUSDT",HYPE:"HYPEUSDT",
  ARB:"ARBUSDT",UNI:"UNIUSDT",AAVE:"AAVEUSDT",
  SHIB:"SHIBUSDT",PEPE:"PEPEUSDT",BONK:"BONKUSDT",WIF:"WIFUSDT"
};

const BINANCE_BASE = "https://api.binance.us/api/v3";

const getToken  = () => sessionStorage.getItem("dash_token");
const setToken  = (t) => sessionStorage.setItem("dash_token", t);
const clearToken= () => sessionStorage.removeItem("dash_token");
const authFetch = (url, opts={}) => fetch(url, {
  ...opts,
  headers: {"Content-Type":"application/json","Authorization":`Bearer ${getToken()}`, ...(opts.headers||{})}
});

const fmt    = (n,d=2) => n==null?"—":Number(n).toLocaleString("en-US",{minimumFractionDigits:d,maximumFractionDigits:d});
const fmtPct = (n) => n==null?"—":`${n>=0?"+":""}${fmt(n)}%`;
const fmtUsd = (n,decimals) => {
  if(n==null) return "—";
  const v=Number(n);
  const d = decimals != null ? decimals : v>=100?2:v>=1?2:v>=0.01?4:6;
  return `$${v.toLocaleString("en-US",{minimumFractionDigits:d,maximumFractionDigits:d})}`;
};

function Sparkline({data,color="#6366f1",width=80,height=28}){
  if(!data||data.length<2) return <svg width={width} height={height}/>;
  const min=Math.min(...data),max=Math.max(...data),range=max-min||1;
  const pts=data.map((v,i)=>{
    const x=(i/(data.length-1))*width;
    const y=height-((v-min)/range)*(height-4)-2;
    return`${x},${y}`;
  }).join(" ");
  return(
    <svg width={width} height={height} style={{overflow:"visible"}}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

function InteractiveChart({history,symbol,timeframeDays,projection,chartType="Area"}){
  const svgRef=useRef(null);
  const [tooltip,setTooltip]=useState(null);
  const filtered=(history||[]).filter(d=>{
    if(timeframeDays>=9999) return true;
    return new Date(d.date).getTime()>=Date.now()-timeframeDays*86400000;
  });
  if(filtered.length<2) return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",color:"rgba(255,255,255,0.3)",fontSize:"13px"}}>
      No chart data
    </div>
  );

  const PROJ_STEPS = 7;
  const buildProjectionCurve = (proj, lastPrice, todayX, futureX) => {
    if(!proj) return null;
    const stepW = (futureX - todayX) / PROJ_STEPS;
    // Center curve: smooth S-curve from lastPrice toward proj.center
    // High/low cone widens as uncertainty grows with time
    const centerPoints = Array.from({length:PROJ_STEPS+1},(_,i)=>{
      const t = i/PROJ_STEPS;
      // Ease-in-out interpolation
      const ease = t<0.5 ? 2*t*t : -1+(4-2*t)*t;
      return lastPrice + (proj.center - lastPrice) * ease;
    });
    const highPoints = Array.from({length:PROJ_STEPS+1},(_,i)=>{
      const t = i/PROJ_STEPS;
      const ease = t<0.5 ? 2*t*t : -1+(4-2*t)*t;
      const center = lastPrice + (proj.center - lastPrice) * ease;
      // Cone widens with sqrt of time — uncertainty grows
      const spread = (proj.high - proj.center) * Math.sqrt(t);
      return center + spread;
    });
    const lowPoints = Array.from({length:PROJ_STEPS+1},(_,i)=>{
      const t = i/PROJ_STEPS;
      const ease = t<0.5 ? 2*t*t : -1+(4-2*t)*t;
      const center = lastPrice + (proj.center - lastPrice) * ease;
      const spread = (proj.center - proj.low) * Math.sqrt(t);
      return center - spread;
    });
    return {centerPoints, highPoints, lowPoints, stepW};
  };

  // Layout: 1100 wide total
  // History: left 680px | Projection zone: right 420px
  const HIST_W = 680;
  const PROJ_W = projection ? 360 : 0;
  const LABEL_W = projection ? 120 : 0;
  const W = HIST_W + PROJ_W + LABEL_W;
  const H = 260;
  const PAD = {top:20, right:0, bottom:32, left:76};
  const chartEnd = HIST_W;      // x coordinate where history ends / today divider
  const projStart = chartEnd;   // projection starts here
  const projEnd = chartEnd + PROJ_W - 10;  // projection ends here (dot goes here)

  const prices=filtered.map(d=>d.price||d.close||0);
  const lastPrice = prices[prices.length-1];

  // Y scale includes projection range
  const allVals = [...prices];
  if(projection){
    allVals.push(projection.high*1.01, projection.low*0.99, projection.center);
  }
  const projMin=Math.min(...allVals)*0.993;
  const projMax=Math.max(...allVals)*1.007;
  const range=projMax-projMin||1;
  const cW=chartEnd-PAD.left;
  const cH=H-PAD.top-PAD.bottom;
  const xS=i=>PAD.left+(i/(filtered.length-1))*cW;
  const yS=v=>PAD.top+cH-((v-projMin)/range)*cH;
  const isUp=prices[prices.length-1]>=prices[0];
  const cc=isUp?"#22c55e":"#ef4444";

  const todayX = xS(filtered.length-1);
  const futureX = projEnd;
  const projCurve = buildProjectionCurve(projection, lastPrice, todayX, futureX);

  const gridLines=Array.from({length:5},(_,i)=>{
    const v=projMin+(range*i/4);
    return{y:yS(v),label:`$${v>=1000?(v/1000).toFixed(1)+"k":v>=1?v.toFixed(2):v.toFixed(4)}`};
  });
  const step=Math.max(1,Math.floor(filtered.length/5));
  const xLabels=[];
  for(let i=0;i<filtered.length;i+=step){
    const d=new Date(filtered[i].date);
    xLabels.push({x:xS(i),label:d.toLocaleDateString("en-US",{month:"short",day:"numeric"})});
  }
  // Add "7D" label at end of projection
  const linePath=filtered.map((d,i)=>`${i===0?"M":"L"}${xS(i)},${yS(prices[i])}`).join(" ");
  const areaPath=linePath+` L${todayX},${PAD.top+cH} L${xS(0)},${PAD.top+cH} Z`;

  // Build SVG paths for projection cone
  const buildPath = (pts) => pts.map((v,i)=>{
    const x = todayX + (i/PROJ_STEPS)*(futureX-todayX);
    return `${i===0?"M":"L"}${x},${yS(v)}`;
  }).join(" ");

  const onMove=e=>{
    const svg=svgRef.current;if(!svg)return;
    const rect=svg.getBoundingClientRect();
    const mx=(e.clientX-rect.left)*(W/rect.width);
    if(mx > chartEnd) return; // no tooltip in projection zone
    const histPct=(mx-PAD.left)/cW;
    const idx=Math.max(0,Math.min(filtered.length-1,Math.round(histPct*(filtered.length-1))));
    setTooltip({x:xS(idx),y:yS(prices[idx]),price:prices[idx],date:filtered[idx].date});
  };

  return(
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
      style={{width:"100%",height:"100%",cursor:"crosshair",overflow:"visible"}}
      onMouseMove={onMove} onMouseLeave={()=>setTooltip(null)} onTouchEnd={()=>setTooltip(null)}>
      <defs>
        <linearGradient id={`g${symbol}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={cc} stopOpacity="0.2"/>
          <stop offset="100%" stopColor={cc} stopOpacity="0"/>
        </linearGradient>
        {projection&&(
          <linearGradient id={`gp${symbol}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={projection.bias==="BULLISH"?"#22c55e":projection.bias==="BEARISH"?"#ef4444":"#a5b4fc"} stopOpacity="0.15"/>
            <stop offset="100%" stopColor={projection.bias==="BULLISH"?"#22c55e":projection.bias==="BEARISH"?"#ef4444":"#a5b4fc"} stopOpacity="0.05"/>
          </linearGradient>
        )}
      </defs>

      {/* Grid */}
      {gridLines.map((g,i)=>(
        <g key={i}>
          <line x1={PAD.left} x2={chartEnd} y1={g.y} y2={g.y} stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          <text x={PAD.left-6} y={g.y+4} fill="rgba(255,255,255,0.3)" fontSize="10" textAnchor="end">{g.label}</text>
        </g>
      ))}
      {xLabels.map((xl,i)=>(
        <text key={i} x={xl.x} y={H-6} fill="rgba(255,255,255,0.3)" fontSize="10" textAnchor="middle">{xl.label}</text>
      ))}

      {/* Historical area + line */}
      {chartType==="Area"&&<path d={areaPath} fill={`url(#g${symbol})`}/>}
      <path d={linePath} fill="none" stroke={cc} strokeWidth="2" strokeLinejoin="round"/>

      {/* Projection cone */}
      {projection&&projCurve&&(()=>{
        const biasColor=projection.bias==="BULLISH"?"#22c55e":projection.bias==="BEARISH"?"#ef4444":"#a5b4fc";
        const centerPath = buildPath(projCurve.centerPoints);
        const highPath   = buildPath(projCurve.highPoints);
        const lowPath    = buildPath(projCurve.lowPoints);
        // Filled cone area: high path forward + low path backward
        const coneFill = buildPath(projCurve.highPoints) + " " +
          projCurve.lowPoints.slice().reverse().map((v,i)=>{
            const ri = projCurve.lowPoints.length-1-i;
            const x = todayX + (ri/PROJ_STEPS)*(futureX-todayX);
            return `L${x},${yS(v)}`;
          }).join(" ") + " Z";

        const confPct = projection.confidence||50;
        // % change from current to target
        const centerPct = projection.current_price
          ? ((projection.center - projection.current_price)/projection.current_price*100)
          : 0;
        const highPct = projection.current_price
          ? ((projection.high - projection.current_price)/projection.current_price*100)
          : 0;
        const lowPct = projection.current_price
          ? ((projection.low - projection.current_price)/projection.current_price*100)
          : 0;

        const finalCenterY = yS(projCurve.centerPoints[PROJ_STEPS]);
        const finalHighY   = yS(projCurve.highPoints[PROJ_STEPS]);
        const finalLowY    = yS(projCurve.lowPoints[PROJ_STEPS]);

        return(
          <g>
            {/* Today divider */}
            <line x1={todayX} x2={todayX} y1={PAD.top} y2={PAD.top+cH}
              stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="3,3"/>
            <text x={todayX+4} y={PAD.top+10} fill="rgba(255,255,255,0.25)" fontSize="9">Today</text>
            <text x={futureX-2} y={PAD.top+10} fill="rgba(255,255,255,0.25)" fontSize="9" textAnchor="end">+7d</text>

            {/* Cone fill */}
            <path d={coneFill} fill={`url(#gp${symbol})`}/>

            {/* High bound */}
            <path d={highPath} fill="none" stroke={biasColor} strokeWidth="1"
              strokeDasharray="3,4" strokeOpacity="0.45"/>
            {/* Low bound */}
            <path d={lowPath} fill="none" stroke={biasColor} strokeWidth="1"
              strokeDasharray="3,4" strokeOpacity="0.45"/>
            {/* Center target line */}
            <path d={centerPath} fill="none" stroke={biasColor} strokeWidth="2"
              strokeDasharray="5,3" strokeOpacity="0.9"/>

            {/* End point dot */}
            <circle cx={futureX} cy={finalCenterY} r="5" fill={biasColor} opacity="0.9"/>
            <circle cx={futureX} cy={finalCenterY} r="9" fill={biasColor} opacity="0.15"/>

            {/* Right-side labels with % change */}
            {/* Collision-aware label placement — enforce min 28px gap */}
            {(()=>{
              const MIN_GAP = 28;
              // Clamp positions so they don't collide
              let hy = finalHighY + 4;
              let cy = finalCenterY - 6;
              let ly = finalLowY + 4;
              if(cy - hy < MIN_GAP) cy = hy + MIN_GAP;
              if(ly - cy < MIN_GAP) ly = cy + MIN_GAP;
              // Keep within chart bounds
              hy = Math.max(PAD.top+4, hy);
              ly = Math.min(PAD.top+cH-4, ly);
              const lx = futureX + 12;
              return(
                <g>
                  <text x={lx} y={hy} fill={biasColor} fontSize="9" fillOpacity="0.75">
                    {fmtUsd(projection.high)}
                  </text>
                  <text x={lx} y={hy+12} fill={biasColor} fontSize="8" fillOpacity="0.5">
                    {highPct>=0?"+":""}{highPct.toFixed(1)}%
                  </text>
                  <text x={lx} y={cy} fill={biasColor} fontSize="10" fontWeight="600">
                    {fmtUsd(projection.center)}
                  </text>
                  <text x={lx} y={cy+13} fill={biasColor} fontSize="9">
                    {centerPct>=0?"+":""}{centerPct.toFixed(1)}% · {confPct}% conf
                  </text>
                  <text x={lx} y={ly} fill={biasColor} fontSize="9" fillOpacity="0.75">
                    {fmtUsd(projection.low)}
                  </text>
                  <text x={lx} y={ly+12} fill={biasColor} fontSize="8" fillOpacity="0.5">
                    {lowPct.toFixed(1)}%
                  </text>
                </g>
              );
            })()}
          </g>
        );
      })()}

      {/* Crosshair tooltip */}
      {tooltip&&(
        <g>
          <line x1={tooltip.x} x2={tooltip.x} y1={PAD.top} y2={PAD.top+cH}
            stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="4,3"/>
          <circle cx={tooltip.x} cy={tooltip.y} r="4" fill={cc} stroke="#1a1a2e" strokeWidth="2"/>
          <rect x={Math.min(tooltip.x+8,W-155)} y={tooltip.y-28} width="145" height="44"
            rx="6" fill="#1e2035" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
          <text x={Math.min(tooltip.x+14,W-149)} y={tooltip.y-12}
            fill="rgba(255,255,255,0.5)" fontSize="10">
            {new Date(tooltip.date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
          </text>
          <text x={Math.min(tooltip.x+14,W-149)} y={tooltip.y+6}
            fill="#fff" fontSize="12" fontWeight="600">
            {fmtUsd(tooltip.price)}
          </text>
        </g>
      )}
    </svg>
  );
}

function TimeoutModal({secondsLeft,onStay}){
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",
      alignItems:"center",justifyContent:"center",zIndex:9999,backdropFilter:"blur(8px)"}}>
      <div style={{background:"#1a1b2e",border:"1px solid rgba(255,255,255,0.1)",
        borderRadius:"16px",padding:"36px",textAlign:"center",maxWidth:"320px",width:"90%"}}>
        <div style={{fontSize:"40px",marginBottom:"12px"}}>⏱️</div>
        <div style={{fontSize:"18px",fontWeight:"700",color:"#fff",marginBottom:"8px"}}>Session Expiring</div>
        <div style={{fontSize:"13px",color:"rgba(255,255,255,0.5)",marginBottom:"20px"}}>
          Logging out in <span style={{color:"#f59e0b",fontWeight:"700",fontSize:"18px"}}>{secondsLeft}s</span>
        </div>
        <button onClick={onStay} style={{background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
          border:"none",color:"#fff",padding:"12px",borderRadius:"10px",
          fontSize:"14px",fontWeight:"600",cursor:"pointer",width:"100%"}}>
          Stay Active
        </button>
      </div>
    </div>
  );
}

function LoginScreen({onLogin}){
  const [pass,setPass]=useState("");
  const [error,setError]=useState("");
  const [loading,setLoading]=useState(false);
  const handle=async()=>{
    setLoading(true);setError("");
    try{
      const r=await fetch(`${API_BASE}/api/login`,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({password:pass})
      });
      if(!r.ok){setError("Incorrect password");setPass("");}
      else{const j=await r.json();setToken(j.token||j.access_token||"ok");onLogin();}
    }catch(e){setError("Connection error");}
    setLoading(false);
  };
  return(
    <div style={{minHeight:"100vh",background:"#0d0e1a",display:"flex",
      alignItems:"center",justifyContent:"center",padding:"20px"}}>
      <div style={{background:"#1a1b2e",border:"1px solid rgba(255,255,255,0.08)",
        borderRadius:"20px",padding:"40px 32px",width:"100%",maxWidth:"360px",textAlign:"center"}}>
        <div style={{fontSize:"36px",marginBottom:"10px"}}>📈</div>
        <div style={{fontSize:"22px",fontWeight:"800",color:"#fff",marginBottom:"4px"}}>DigitalBalm</div>
        <div style={{fontSize:"12px",color:"rgba(255,255,255,0.4)",marginBottom:"28px"}}>V11 Crypto Intelligence</div>
        <input type="password" value={pass} placeholder="Enter password"
          onChange={e=>{setPass(e.target.value);setError("");}}
          onKeyDown={e=>e.key==="Enter"&&handle()}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${error?"#ef4444":"rgba(255,255,255,0.1)"}`,
            borderRadius:"10px",padding:"12px 16px",color:"#fff",fontSize:"14px",
            boxSizing:"border-box",marginBottom:"8px",outline:"none"}}/>
        {error&&<div style={{color:"#ef4444",fontSize:"12px",marginBottom:"8px"}}>{error}</div>}
        <button onClick={handle} disabled={loading}
          style={{width:"100%",background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
            border:"none",color:"#fff",padding:"13px",borderRadius:"10px",
            fontSize:"15px",fontWeight:"600",cursor:"pointer",marginTop:"4px",
            opacity:loading?0.7:1}}>
          {loading?"Signing in...":"Sign In"}
        </button>
      </div>
    </div>
  );
}

export default function App(){
  const [loggedIn,setLoggedIn]=useState(!!getToken());
  const [signals,setSignals]=useState([]);
  const [positions,setPositions]=useState([]);
  const [market,setMarket]=useState({});
  const [projections,setProjections]=useState({});
  const [selectedCoin,setSelectedCoin]=useState("BTC");
  const [timeframe,setTimeframe]=useState(30);
  const [chartType,setChartType]=useState('Area');
  const [history,setHistory]=useState({});
  const [activeTab,setActiveTab]=useState("overview");
  const [showTimeout,setShowTimeout]=useState(false);
  const [timeoutSecs,setTimeoutSecs]=useState(10);
  const [lastUpdate,setLastUpdate]=useState(null);
  const inactRef=useRef(null);const countRef=useRef(null);

  const logout=useCallback(()=>{
    clearToken();setLoggedIn(false);setShowTimeout(false);
    clearTimeout(inactRef.current);clearInterval(countRef.current);
  },[]);

  const resetTimer=useCallback(()=>{
    if(!loggedIn)return;
    setShowTimeout(false);clearTimeout(inactRef.current);clearInterval(countRef.current);
    inactRef.current=setTimeout(()=>{
      setTimeoutSecs(10);setShowTimeout(true);let s=10;
      countRef.current=setInterval(()=>{
        s--;setTimeoutSecs(s);
        if(s<=0){clearInterval(countRef.current);logout();}
      },1000);
    },INACTIVITY_TIMEOUT-10000);
  },[loggedIn,logout]);

  useEffect(()=>{
    if(!loggedIn)return;
    const evts=["mousemove","mousedown","keydown","scroll","touchstart","click"];
    evts.forEach(e=>window.addEventListener(e,resetTimer,{passive:true}));
    resetTimer();
    return()=>{evts.forEach(e=>window.removeEventListener(e,resetTimer));
      clearTimeout(inactRef.current);clearInterval(countRef.current);};
  },[loggedIn,resetTimer]);

  const fetchAll=useCallback(async()=>{
    if(!getToken())return;
    try{
      const [sRes,pRes,mRes,prRes]=await Promise.all([
        authFetch(`${API_BASE}/api/signals`),
        authFetch(`${API_BASE}/api/positions`),
        authFetch(`${API_BASE}/api/market`),
        authFetch(`${API_BASE}/api/projections`),
      ]);
      if(sRes.status===401){logout();return;}
      if(sRes.ok) setSignals(await sRes.json());
      if(pRes.ok) setPositions(await pRes.json());
      if(mRes.ok) setMarket(await mRes.json());
      if(prRes.ok){
        const j=await prRes.json();
        // Handle both {symbol:proj} and {id:{...symbol}} formats
        const normalized={};
        Object.values(j).forEach(p=>{if(p&&p.symbol)normalized[p.symbol]=p;});
        setProjections(normalized);
      }
      setLastUpdate(new Date());
    }catch(e){console.error(e);}
  },[logout]);

  const fetchHistory=useCallback(async(coin)=>{
    if(history[coin])return;
    const pair=BINANCE_PAIRS[coin];
    if(!pair) return;
    const parseCandles = j => j.map(k=>({
      date:  new Date(k[0]).toISOString(),
      price: parseFloat(k[4]),
      open:  parseFloat(k[1]),
      high:  parseFloat(k[2]),
      low:   parseFloat(k[3]),
      volume:parseFloat(k[5]),
    }));
    // Try Binance US first, fall back to global Binance for coins not listed on .us
    const urls = [
      `${BINANCE_BASE}/klines?symbol=${pair}&interval=1d&limit=365`,
      `https://api.binance.com/api/v3/klines?symbol=${pair}&interval=1d&limit=365`,
    ];
    for(const url of urls){
      try{
        const r=await fetch(url);
        if(r.ok){
          const j=await r.json();
          if(Array.isArray(j)&&j.length>0){
            setHistory(h=>({...h,[coin]:parseCandles(j)}));
            return;
          }
        }
      }catch(e){}
    }
    console.warn("No chart data for",coin,"on either Binance endpoint");
  },[history]);

  useEffect(()=>{
    if(!loggedIn)return;
    fetchAll();
    const iv=setInterval(fetchAll,90000);
    return()=>clearInterval(iv);
  },[loggedIn,fetchAll]);

  useEffect(()=>{
    if(!loggedIn||!selectedCoin)return;
    fetchHistory(selectedCoin);
    // Stagger prefetch other coins
    const others=ALL_COINS.filter(c=>c!==selectedCoin&&!history[c]);
    others.forEach((coin,i)=>setTimeout(()=>fetchHistory(coin),(i+1)*300));
  },[loggedIn,selectedCoin]);

  if(!loggedIn) return <LoginScreen onLogin={()=>setLoggedIn(true)}/>;

  const fg        = market.fear_greed||{};
  const fgVal     = fg.value||0;
  const fgColor   = fgVal>=75?"#22c55e":fgVal>=55?"#f59e0b":fgVal>=30?"#f97316":"#ef4444";
  const coinData  = market.coin_data||market.prices||{};
  const totalPnl  = positions.reduce((s,p)=>s+(p.pnl_usd||p.pnl||0),0);
  const openCount = positions.filter(p=>["OPEN","open"].includes(p.status)).length;
  const proj      = projections[selectedCoin]||null;

  // Mobile detection
  const isMobile = window.innerWidth < 768;

  const card={
    background:"#1a1b2e",
    border:"1px solid rgba(255,255,255,0.07)",
    borderRadius:"12px",
    padding:"14px"
  };

  const tabStyle=(t)=>({
    flex:1,background:activeTab===t?"rgba(99,102,241,0.2)":"transparent",
    border:"none",
    borderTop:activeTab===t?"2px solid #6366f1":"2px solid transparent",
    color:activeTab===t?"#a5b4fc":"rgba(255,255,255,0.4)",
    padding:"10px 4px 8px",
    fontSize:"11px",fontWeight:"500",cursor:"pointer",
    display:"flex",flexDirection:"column",alignItems:"center",gap:"3px"
  });


  return(
    <div style={{minHeight:"100vh",background:"#0d0e1a",color:"#fff",
      fontFamily:"system-ui,sans-serif",maxWidth:"100vw",overflowX:"hidden"}}>
      {showTimeout&&<TimeoutModal secondsLeft={timeoutSecs} onStay={resetTimer}/>}

      {/* Header */}
      <div style={{background:"#111220",borderBottom:"1px solid rgba(255,255,255,0.06)",
        padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",
        position:"sticky",top:0,zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
          <span style={{fontSize:"16px"}}>📈</span>
          <span style={{fontSize:"14px",fontWeight:"800",letterSpacing:"-0.5px"}}>DigitalBalm</span>
          <span style={{fontSize:"9px",color:"rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.05)",
            padding:"2px 6px",borderRadius:"20px"}}>V11</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
          <div style={{fontSize:"10px",color:"rgba(255,255,255,0.3)"}}>
            {lastUpdate?lastUpdate.toLocaleTimeString():"—"}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"4px"}}>
            <div style={{width:"5px",height:"5px",borderRadius:"50%",background:"#22c55e",
              boxShadow:"0 0 4px #22c55e"}}/>
          </div>
          <button onClick={logout} style={{background:"rgba(239,68,68,0.1)",
            border:"1px solid rgba(239,68,68,0.2)",color:"#f87171",
            padding:"4px 10px",borderRadius:"6px",fontSize:"11px",cursor:"pointer"}}>
            Out
          </button>
        </div>
      </div>

      {/* Desktop layout: sidebar + main | Mobile: full width stacked */}
      <div style={{display:"flex",height:"calc(100vh - 53px - 52px)"}}>

        {/* Sidebar — desktop only */}
        {!isMobile&&(
          <div style={{width:"240px",borderRight:"1px solid rgba(255,255,255,0.06)",
            overflowY:"auto",padding:"10px 8px",flexShrink:0}}>
            {/* F&G */}
            <div style={{...card,marginBottom:"8px",padding:"10px 12px"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:"5px"}}>
                <span style={{fontSize:"10px",color:"rgba(255,255,255,0.4)"}}>Fear & Greed</span>
                <span style={{fontSize:"10px",color:fgColor,fontWeight:"600"}}>{fg.classification||"—"}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                <div style={{fontSize:"22px",fontWeight:"800",color:fgColor}}>{fgVal}</div>
                <div style={{flex:1,height:"4px",background:"rgba(255,255,255,0.06)",borderRadius:"2px"}}>
                  <div style={{width:`${fgVal}%`,height:"100%",
                    background:"linear-gradient(90deg,#ef4444,#f59e0b,#22c55e)",borderRadius:"2px"}}/>
                </div>
              </div>
            </div>
            {/* Stats */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"5px",marginBottom:"8px"}}>
              {[{label:"Open",value:openCount,color:"#6366f1"},
                {label:"P&L",value:fmtUsd(totalPnl),color:totalPnl>=0?"#22c55e":"#ef4444"}
              ].map(s=>(
                <div key={s.label} style={{...card,padding:"8px",textAlign:"center"}}>
                  <div style={{fontSize:"9px",color:"rgba(255,255,255,0.4)",marginBottom:"2px"}}>{s.label}</div>
                  <div style={{fontSize:"13px",fontWeight:"700",color:s.color}}>{s.value}</div>
                </div>
              ))}
            </div>
            {/* Coin list */}
            <div style={{fontSize:"9px",color:"rgba(255,255,255,0.3)",
              textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"5px",padding:"0 3px"}}>
              All Assets
            </div>
            {ALL_COINS.map(sym=>{
              const cd=coinData[sym]||{};
              const pct=cd.change_24h||cd.price_change_24h;
              const isUp=(pct||0)>=0;
              const isActive=selectedCoin===sym;
              const tier=COIN_TIERS[sym]||"core";
              const pos=positions.find(p=>(p.symbol||p.coin)===sym);
              return(
                <div key={sym} onClick={()=>{setSelectedCoin(sym);setActiveTab("chart");}}
                  style={{display:"flex",alignItems:"center",padding:"7px 10px",
                    borderRadius:"8px",cursor:"pointer",marginBottom:"1px",
                    background:isActive?"rgba(99,102,241,0.12)":"transparent",
                    border:isActive?"1px solid rgba(99,102,241,0.3)":"1px solid transparent",
                    transition:"all 0.1s"}}>
                  <div style={{width:"4px",height:"28px",borderRadius:"2px",
                    background:TIER_COLORS[tier],marginRight:"8px",opacity:0.7,flexShrink:0}}/>
                  <div style={{width:"34px",fontSize:"11px",fontWeight:"700",
                    color:"rgba(255,255,255,0.9)",fontFamily:"monospace"}}>{sym}</div>
                  <div style={{flex:1,fontSize:"10px",color:"rgba(255,255,255,0.5)"}}>
                    {cd.price?fmtUsd(cd.price):""}
                  </div>
                  <div style={{fontSize:"10px",fontWeight:"600",
                    color:isUp?"#22c55e":"#ef4444",marginRight:"6px"}}>
                    {pct!=null?`${isUp?"+":""}${Number(pct).toFixed(1)}%`:""}
                  </div>
                  <Sparkline data={cd.sparkline||cd.sparkline_7d}
                    color={isUp?"#22c55e":"#ef4444"} width={44} height={20}/>
                  {pos&&<div style={{width:"4px",height:"4px",borderRadius:"50%",
                    background:"#6366f1",marginLeft:"4px",flexShrink:0}}/>}
                </div>
              );
            })}
          </div>
        )}

        {/* Main content */}
        <div style={{flex:1,overflowY:"auto",padding:isMobile?"12px":"16px 18px"}}>

          {/* Mobile coin selector */}
          {isMobile&&(
            <div style={{marginBottom:"12px",overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
              <div style={{display:"flex",gap:"6px",paddingBottom:"4px",minWidth:"max-content"}}>
                {ALL_COINS.map(sym=>{
                  const tier=COIN_TIERS[sym]||"core";
                  const isActive=selectedCoin===sym;
                  const cd=coinData[sym]||{};
                  const pct=cd.change_24h||0;
                  const isUp=pct>=0;
                  return(
                    <button key={sym} onClick={()=>{setSelectedCoin(sym);setActiveTab("chart");}}
                      style={{background:isActive?TIER_COLORS[tier]+"33":"rgba(255,255,255,0.05)",
                        border:`1px solid ${isActive?TIER_COLORS[tier]:"rgba(255,255,255,0.08)"}`,
                        borderRadius:"20px",padding:"5px 10px",cursor:"pointer",
                        display:"flex",flexDirection:"column",alignItems:"center",gap:"1px",
                        minWidth:"52px",flexShrink:0}}>
                      <span style={{fontSize:"11px",fontWeight:"700",
                        color:isActive?"#fff":"rgba(255,255,255,0.7)"}}>{sym}</span>
                      <span style={{fontSize:"9px",color:isUp?"#22c55e":"#ef4444"}}>
                        {pct!=null?`${isUp?"+":""}${Number(pct).toFixed(1)}%`:""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Overview */}
          {activeTab==="overview"&&(
            <div>
              {/* Macro cards */}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",
                gap:"8px",marginBottom:"12px"}}>
                {[
                  {label:"F&G",value:fgVal,sub:fg.classification||"—",color:fgColor},
                  {label:"BTC Dom",value:`${market.btc_dominance?.toFixed?.(1)||"—"}%`,
                    sub:market.regime||"—",color:"#a5b4fc"},
                  {label:"Market",value:market.monthly_regime||market.regime||"—",
                    sub:market.macro_mode||"—",color:"#f59e0b"},
                  {label:"MVRV",value:market.mvrv_zone||"—",sub:"on-chain",color:"#22c55e"},
                ].map(m=>(
                  <div key={m.label} style={card}>
                    <div style={{fontSize:"10px",color:"rgba(255,255,255,0.4)",marginBottom:"3px"}}>{m.label}</div>
                    <div style={{fontSize:"16px",fontWeight:"700",color:m.color}}>{m.value}</div>
                    <div style={{fontSize:"10px",color:"rgba(255,255,255,0.35)",marginTop:"2px"}}>{m.sub}</div>
                  </div>
                ))}
              </div>

              {/* Open positions */}
              <div style={{...card,marginBottom:"10px"}}>
                <div style={{fontSize:"11px",fontWeight:"600",color:"rgba(255,255,255,0.6)",marginBottom:"10px"}}>
                  Open Positions ({openCount})
                </div>
                {positions.filter(p=>["OPEN","open"].includes(p.status)).length===0
                  ?<div style={{color:"rgba(255,255,255,0.3)",fontSize:"12px",textAlign:"center",padding:"16px 0"}}>
                    No open positions
                  </div>
                  :positions.filter(p=>["OPEN","open"].includes(p.status)).map((pos,i)=>{
                    const pct=pos.pnl_pct||0;
                    return(
                      <div key={i} style={{display:"flex",alignItems:"center",
                        padding:"8px 10px",background:"rgba(255,255,255,0.03)",
                        borderRadius:"8px",marginBottom:"5px"}}>
                        <div style={{width:"36px",fontWeight:"700",fontSize:"12px"}}>{pos.symbol||pos.coin}</div>
                        <div style={{flex:1,fontSize:"10px",color:"rgba(255,255,255,0.5)"}}>
                          {pos.profile?.toUpperCase?.()||""} · ${(pos.position_usd||0).toFixed?.(0)}
                        </div>
                        <div style={{fontSize:"12px",fontWeight:"600",
                          color:pct>=0?"#22c55e":"#ef4444"}}>{fmtPct(pct)}</div>
                      </div>
                    );
                  })
                }
              </div>

              {/* Recent signals */}
              <div style={card}>
                <div style={{fontSize:"11px",fontWeight:"600",color:"rgba(255,255,255,0.6)",marginBottom:"10px"}}>
                  Recent Signals
                </div>
                {signals.length===0
                  ?<div style={{color:"rgba(255,255,255,0.3)",fontSize:"12px",textAlign:"center",padding:"16px 0"}}>
                    No signals this scan
                  </div>
                  :signals.slice(0,6).map((sig,i)=>{
                    const dir=sig.direction||sig.analysis?.direction;
                    const conf=sig.confidence||sig.analysis?.confidence;
                    const color=dir==="BUY"?"#22c55e":dir==="SELL"?"#ef4444":"#94a3b8";
                    return(
                      <div key={i} style={{display:"flex",alignItems:"center",
                        padding:"7px 10px",background:"rgba(255,255,255,0.03)",
                        borderRadius:"7px",marginBottom:"4px"}}>
                        <div style={{width:"5px",height:"5px",borderRadius:"50%",
                          background:color,marginRight:"8px",flexShrink:0}}/>
                        <div style={{width:"36px",fontWeight:"700",fontSize:"11px"}}>{sig.symbol||sig.coin}</div>
                        <div style={{flex:1,fontSize:"10px",color:"rgba(255,255,255,0.4)"}}>
                          {conf||"—"}% conf
                        </div>
                        <div style={{fontSize:"11px",fontWeight:"600",color}}>{dir||"HOLD"}</div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          )}

          {/* Chart */}
          {activeTab==="chart"&&(
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                marginBottom:"12px",flexWrap:"wrap",gap:"8px"}}>
                <div>
                  <span style={{fontSize:"18px",fontWeight:"800"}}>{selectedCoin}</span>
                  {coinData[selectedCoin]?.price&&(
                    <span style={{fontSize:"15px",color:"rgba(255,255,255,0.6)",marginLeft:"8px"}}>
                      {fmtUsd(coinData[selectedCoin].price)}
                    </span>
                  )}
                  {coinData[selectedCoin]?.change_24h!=null&&(
                    <span style={{fontSize:"11px",marginLeft:"8px",fontWeight:"600",
                      color:coinData[selectedCoin].change_24h>=0?"#22c55e":"#ef4444"}}>
                      {fmtPct(coinData[selectedCoin].change_24h)} 24h
                    </span>
                  )}
                  {COIN_TIERS[selectedCoin]&&(
                    <span style={{fontSize:"9px",marginLeft:"8px",padding:"2px 6px",
                      borderRadius:"10px",background:`${TIER_COLORS[COIN_TIERS[selectedCoin]]}22`,
                      color:TIER_COLORS[COIN_TIERS[selectedCoin]]}}>
                      {COIN_TIERS[selectedCoin]}
                    </span>
                  )}
                </div>
                <div style={{display:"flex",gap:"6px",flexWrap:"wrap",justifyContent:"flex-end"}}>
                <div style={{display:"flex",background:"rgba(255,255,255,0.05)",
                  borderRadius:"7px",padding:"2px"}}>
                  {["Line","Area"].map(ct=>(
                    <button key={ct} onClick={()=>setChartType(ct)}
                      style={{background:chartType===ct?"rgba(99,102,241,0.3)":"transparent",
                        border:"none",color:chartType===ct?"#a5b4fc":"rgba(255,255,255,0.4)",
                        padding:"4px 10px",borderRadius:"5px",fontSize:"10px",cursor:"pointer",fontWeight:"500"}}>
                      {ct}
                    </button>
                  ))}
                </div>
                <div style={{display:"flex",background:"rgba(255,255,255,0.05)",
                  borderRadius:"7px",padding:"2px",overflowX:"auto"}}>
                  {TIMEFRAMES.map(tf=>(
                    <button key={tf.label} onClick={()=>setTimeframe(tf.days)}
                      style={{background:timeframe===tf.days?"rgba(99,102,241,0.3)":"transparent",
                        border:"none",color:timeframe===tf.days?"#a5b4fc":"rgba(255,255,255,0.4)",
                        padding:"4px 8px",borderRadius:"5px",fontSize:"10px",cursor:"pointer",
                        fontWeight:"500",whiteSpace:"nowrap"}}>
                      {tf.label}
                    </button>
                  ))}
                </div>
                </div>
              </div>

              <div style={{...card,height:isMobile?"220px":"280px",marginBottom:"10px"}}>
                {history[selectedCoin]
                  ?<InteractiveChart history={history[selectedCoin]} symbol={selectedCoin}
                      timeframeDays={timeframe} projection={proj} chartType={chartType}/>
                  :<div style={{display:"flex",alignItems:"center",justifyContent:"center",
                    height:"100%",color:"rgba(255,255,255,0.3)",fontSize:"13px"}}>
                    Loading chart...
                  </div>
                }
              </div>

              {/* 7-day projection inline */}
              {!proj&&(
                <div style={{...card,marginBottom:"10px",padding:"10px 14px",
                  borderLeft:"3px solid rgba(255,255,255,0.1)",
                  display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{fontSize:"11px",color:"rgba(255,255,255,0.3)"}}>
                    🔮 7-Day projection
                  </div>
                  <div style={{fontSize:"10px",color:"rgba(255,255,255,0.2)"}}>
                    Generated next scan for open positions
                  </div>
                </div>
              )}
              {proj&&(
                <div style={{...card,marginBottom:"10px",
                  borderLeft:`3px solid ${proj.bias==="BULLISH"?"#22c55e":proj.bias==="BEARISH"?"#ef4444":"#a5b4fc"}`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
                    <div style={{fontSize:"11px",fontWeight:"600",color:"rgba(255,255,255,0.6)"}}>
                      🔮 7-Day Projection
                    </div>
                    <span style={{fontSize:"10px",fontWeight:"600",padding:"2px 8px",borderRadius:"20px",
                      color:proj.bias==="BULLISH"?"#22c55e":proj.bias==="BEARISH"?"#ef4444":"#a5b4fc",
                      background:proj.bias==="BULLISH"?"rgba(34,197,94,0.1)":"rgba(239,68,68,0.1)"}}>
                      {proj.bias}
                    </span>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"6px",marginBottom:"8px"}}>
                    {[
                      {label:"Now",value:fmtUsd(proj.current_price),color:"#f59e0b"},
                      {label:"Target",value:fmtUsd(proj.center),color:"#a5b4fc"},
                      {label:"Bull",value:fmtUsd(proj.high),color:"#22c55e"},
                      {label:"Bear",value:fmtUsd(proj.low),color:"#ef4444"},
                    ].map(s=>(
                      <div key={s.label} style={{background:"rgba(255,255,255,0.03)",
                        borderRadius:"7px",padding:"7px",textAlign:"center"}}>
                        <div style={{fontSize:"9px",color:"rgba(255,255,255,0.4)",marginBottom:"2px"}}>{s.label}</div>
                        <div style={{fontSize:"12px",fontWeight:"700",color:s.color}}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:"11px",color:"rgba(255,255,255,0.5)",lineHeight:"1.5"}}>
                    {proj.rationale}
                  </div>
                  <div style={{fontSize:"9px",color:"rgba(255,255,255,0.2)",marginTop:"6px",textAlign:"right"}}>
                    Confidence {proj.confidence}% · {new Date(proj.generated_at).toLocaleString()}
                  </div>
                </div>
              )}

              {/* Coin stats */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"6px"}}>
                {[
                  {label:"24h High",value:fmtUsd(coinData[selectedCoin]?.high_24h)},
                  {label:"24h Low",value:fmtUsd(coinData[selectedCoin]?.low_24h)},
                  {label:"Volume",value:coinData[selectedCoin]?.volume_24h?
                    `$${(coinData[selectedCoin].volume_24h/1e9).toFixed(2)}B`:"—"},
                  {label:"Market Cap",value:coinData[selectedCoin]?.market_cap?
                    `$${(coinData[selectedCoin].market_cap/1e9).toFixed(1)}B`:"—"},
                ].map(s=>(
                  <div key={s.label} style={card}>
                    <div style={{fontSize:"10px",color:"rgba(255,255,255,0.4)",marginBottom:"2px"}}>{s.label}</div>
                    <div style={{fontSize:"13px",fontWeight:"600"}}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Positions */}
          {activeTab==="positions"&&(
            <div style={card}>
              <div style={{fontSize:"11px",fontWeight:"600",color:"rgba(255,255,255,0.6)",marginBottom:"12px"}}>
                All Positions
              </div>
              {positions.length===0
                ?<div style={{color:"rgba(255,255,255,0.3)",fontSize:"13px",textAlign:"center",padding:"32px 0"}}>
                  No positions on record
                </div>
                :<div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px",minWidth:"500px"}}>
                    <thead>
                      <tr style={{color:"rgba(255,255,255,0.4)",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
                        {["Symbol","Profile","Entry","P&L%","P&L$","Stop","TP","Status"].map(h=>(
                          <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:"9px",fontWeight:"500"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((pos,i)=>{
                        const pct=pos.pnl_pct||0;
                        const col=pct>=0?"#22c55e":"#ef4444";
                        return(
                          <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                            <td style={{padding:"8px",fontWeight:"700"}}>{pos.symbol||pos.coin}</td>
                            <td style={{padding:"8px",color:"rgba(255,255,255,0.5)",fontSize:"9px",textTransform:"uppercase"}}>{pos.profile||"—"}</td>
                            <td style={{padding:"8px"}}>{fmtUsd(pos.entry_price||pos.entry)}</td>
                            <td style={{padding:"8px",color:col,fontWeight:"600"}}>{fmtPct(pct)}</td>
                            <td style={{padding:"8px",color:col,fontWeight:"600"}}>{fmtUsd(pos.pnl_usd||pos.pnl)}</td>
                            <td style={{padding:"8px",color:"#ef4444",fontSize:"9px"}}>{fmtUsd(pos.stop_loss)}</td>
                            <td style={{padding:"8px",color:"#22c55e",fontSize:"9px"}}>{fmtUsd(pos.take_profit)}</td>
                            <td style={{padding:"8px"}}>
                              <span style={{background:["OPEN","open"].includes(pos.status)?"rgba(99,102,241,0.15)":"rgba(255,255,255,0.05)",
                                color:["OPEN","open"].includes(pos.status)?"#a5b4fc":"rgba(255,255,255,0.4)",
                                padding:"2px 6px",borderRadius:"4px",fontSize:"9px"}}>
                                {pos.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              }
            </div>
          )}

          {/* Signals */}
          {activeTab==="signals"&&(
            <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
              {signals.length===0
                ?<div style={{...card,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"40px"}}>
                  No signals this scan
                </div>
                :signals.map((sig,i)=>{
                  const dir=sig.direction||sig.analysis?.direction;
                  const conf=sig.confidence||sig.analysis?.confidence;
                  const color=dir==="BUY"?"#22c55e":dir==="SELL"?"#ef4444":"#94a3b8";
                  const boosters=sig.active_boosters||[];
                  const delta=sig.booster_delta||sig.analysis?.booster_delta||0;
                  return(
                    <div key={i} style={{...card,borderLeft:`3px solid ${color}`}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                          <span style={{fontWeight:"800",fontSize:"14px"}}>{sig.symbol||sig.coin}</span>
                          <span style={{background:`${color}22`,color,padding:"2px 7px",
                            borderRadius:"5px",fontSize:"10px",fontWeight:"600"}}>{dir||"HOLD"}</span>
                          {COIN_TIERS[sig.symbol]&&(
                            <span style={{fontSize:"9px",padding:"1px 5px",borderRadius:"8px",
                              background:`${TIER_COLORS[COIN_TIERS[sig.symbol]]}22`,
                              color:TIER_COLORS[COIN_TIERS[sig.symbol]]}}>
                              {COIN_TIERS[sig.symbol]}
                            </span>
                          )}
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:"10px",color:"rgba(255,255,255,0.5)"}}>
                            {conf||"—"}% conf
                          </div>
                          {delta!==0&&(
                            <div style={{fontSize:"9px",color:delta>0?"#22c55e":"#f59e0b"}}>
                              {delta>0?"+":""}{(delta*100).toFixed(0)}% size
                            </div>
                          )}
                        </div>
                      </div>
                      {(sig.reasoning||sig.analysis?.reasoning)&&(
                        <div style={{fontSize:"11px",color:"rgba(255,255,255,0.55)",lineHeight:"1.5",
                          background:"rgba(255,255,255,0.03)",padding:"8px",borderRadius:"6px",marginBottom:"6px"}}>
                          {sig.reasoning||sig.analysis?.reasoning}
                        </div>
                      )}
                      {boosters.length>0&&(
                        <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
                          {boosters.slice(0,6).map(b=>(
                            <span key={b} style={{background:"rgba(99,102,241,0.15)",color:"#a5b4fc",
                              padding:"1px 6px",borderRadius:"4px",fontSize:"9px"}}>
                              {b.replace(/_/g," ")}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              }
            </div>
          )}

          {/* Projections */}
          {activeTab==="projections"&&(
            <div>
              <div style={{fontSize:"11px",color:"rgba(255,255,255,0.4)",marginBottom:"12px"}}>
                AI 7-day price projections · Updated each scan
              </div>
              {Object.keys(projections).length===0
                ?<div style={{...card,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"48px"}}>
                  <div style={{fontSize:"28px",marginBottom:"10px"}}>🔮</div>
                  <div>No projections yet</div>
                </div>
                :<div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                  {Object.values(projections).sort((a,b)=>a.symbol.localeCompare(b.symbol)).map((proj,i)=>{
                    const biasColor=proj.bias==="BULLISH"?"#22c55e":proj.bias==="BEARISH"?"#ef4444":"#a5b4fc";
                    const upside=proj.current_price?((proj.high-proj.current_price)/proj.current_price*100):0;
                    const downside=proj.current_price?((proj.current_price-proj.low)/proj.current_price*100):0;
                    return(
                      <div key={proj.symbol} style={{...card,borderLeft:`3px solid ${biasColor}`,cursor:"pointer"}}
                        onClick={()=>{setSelectedCoin(proj.symbol);setActiveTab("chart");}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                            <span style={{fontWeight:"800",fontSize:"16px"}}>{proj.symbol}</span>
                            <span style={{background:`${biasColor}18`,color:biasColor,
                              padding:"2px 8px",borderRadius:"20px",fontSize:"10px",fontWeight:"600"}}>
                              {proj.bias}
                            </span>
                          </div>
                          <div style={{fontSize:"12px",fontWeight:"700"}}>{proj.confidence}%</div>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"6px",marginBottom:"8px"}}>
                          {[
                            {label:"Now",value:fmtUsd(proj.current_price),color:"#f59e0b"},
                            {label:"Target",value:fmtUsd(proj.center),color:"#a5b4fc"},
                            {label:`+${upside.toFixed(1)}%`,value:fmtUsd(proj.high),color:"#22c55e"},
                            {label:`-${downside.toFixed(1)}%`,value:fmtUsd(proj.low),color:"#ef4444"},
                          ].map(s=>(
                            <div key={s.label} style={{background:"rgba(255,255,255,0.03)",
                              borderRadius:"7px",padding:"7px",textAlign:"center"}}>
                              <div style={{fontSize:"9px",color:"rgba(255,255,255,0.4)",marginBottom:"2px"}}>{s.label}</div>
                              <div style={{fontSize:"11px",fontWeight:"700",color:s.color}}>{s.value}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{fontSize:"11px",color:"rgba(255,255,255,0.55)",lineHeight:"1.5"}}>
                          {proj.rationale}
                        </div>
                        <div style={{fontSize:"9px",color:"rgba(255,255,255,0.2)",marginTop:"6px",textAlign:"right"}}>
                          {new Date(proj.generated_at).toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              }
            </div>
          )}

        </div>
      </div>

      {/* Bottom tab bar — always visible */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,
        background:"#111220",borderTop:"1px solid rgba(255,255,255,0.08)",
        display:"flex",zIndex:100,paddingBottom:"env(safe-area-inset-bottom,0px)"}}>
        {[
          {key:"overview",label:"Overview",icon:"◉"},
          {key:"chart",label:"Chart",icon:"▦"},
          {key:"positions",label:"Positions",icon:"◈"},
          {key:"signals",label:"Signals",icon:"◆"},
          {key:"projections",label:"Outlook",icon:"◇"},
        ].map(t=>(
          <button key={t.key} onClick={()=>setActiveTab(t.key)} style={tabStyle(t.key)}>
            <span style={{fontSize:"14px"}}>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
